import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { useGetGameDetailsQuery } from "../store/api/mlbApi";
import TeamLogo from "../components/TeamLogo";
import PlayerNameCell from "../components/PlayerNameCell";
import {
  buildBattingBoxRows,
  buildPitchingBoxRows,
  formatPitchingDecisionLine,
} from "../utils/gameBoxScoreRows";
import { getPlayerPath } from "../utils/fanPage";
import { BRAND_COLORS, brandRgba } from "../utils/brandAssets";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  formatGridStatValue,
  getResultsGridSx,
  sortedClassFor,
} from "../utils/resultsGrid";

const formatGameDate = (gameDate, officialDate) => {
  const value = gameDate || officialDate;
  if (!value) return "Date TBD";

  try {
    return new Date(value).toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return value;
  }
};

const getStatusChip = (status) => {
  if (!status) {
    return { label: "Unknown", color: "default" };
  }

  switch (status.abstractGameState) {
    case "Final":
      return { label: "Final", color: "success" };
    case "Live":
      return { label: status.detailedState || "Live", color: "warning" };
    case "Preview":
      return { label: "Scheduled", color: "info" };
    case "Postponed":
      return { label: "Postponed", color: "error" };
    default:
      return { label: status.detailedState || status.abstractGameState, color: "default" };
  }
};

const TeamScoreBlock = ({ team, side, score, isWinner }) => {
  if (!team?.team) return null;

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: side === "away" ? "flex-start" : "flex-end",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexDirection: side === "home" ? "row-reverse" : "row",
        }}
      >
        <TeamLogo
          mlbTeamId={team.team.id}
          teamName={team.team.name}
          size={56}
          alt={`${team.team.name} logo`}
        />
        <Box sx={{ textAlign: side === "home" ? "right" : "left" }}>
          <Typography variant="h5" fontWeight={700}>
            {team.team.name}
          </Typography>
          {isWinner && (
            <Chip label="Winner" size="small" color="success" variant="outlined" sx={{ mt: 0.5 }} />
          )}
        </Box>
      </Box>
      <Typography variant="h2" fontWeight={800} color="primary.main">
        {score ?? "–"}
      </Typography>
    </Box>
  );
};

const LinescoreTable = ({ linescore, awayName, homeName }) => {
  const innings = linescore?.innings || [];

  if (innings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Linescore not available yet.
      </Typography>
    );
  }

  const awayTotal = linescore?.teams?.away?.runs ?? innings.reduce((sum, inning) => sum + (inning.away?.runs || 0), 0);
  const homeTotal = linescore?.teams?.home?.runs ?? innings.reduce((sum, inning) => sum + (inning.home?.runs || 0), 0);
  const awayHits = linescore?.teams?.away?.hits ?? "–";
  const homeHits = linescore?.teams?.home?.hits ?? "–";
  const awayErrors = linescore?.teams?.away?.errors ?? "–";
  const homeErrors = linescore?.teams?.home?.errors ?? "–";

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Team</TableCell>
            {innings.map((inning) => (
              <TableCell key={inning.num} align="center">
                {inning.num}
              </TableCell>
            ))}
            <TableCell align="center" sx={{ fontWeight: 700 }}>
              R
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }}>
              H
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }}>
              E
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>{awayName}</TableCell>
            {innings.map((inning) => (
              <TableCell key={`away-${inning.num}`} align="center">
                {inning.away?.runs ?? 0}
              </TableCell>
            ))}
            <TableCell align="center" sx={{ fontWeight: 700 }}>
              {awayTotal}
            </TableCell>
            <TableCell align="center">{awayHits}</TableCell>
            <TableCell align="center">{awayErrors}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{homeName}</TableCell>
            {innings.map((inning) => (
              <TableCell key={`home-${inning.num}`} align="center">
                {inning.home?.runs ?? 0}
              </TableCell>
            ))}
            <TableCell align="center" sx={{ fontWeight: 700 }}>
              {homeTotal}
            </TableCell>
            <TableCell align="center">{homeHits}</TableCell>
            <TableCell align="center">{homeErrors}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const BOX_TABLE_HEIGHT = 560;

const statColumn = (field, headerName, sortedField, format = "count", width = 80) => ({
  field,
  headerName,
  width,
  type: "number",
  align: "right",
  headerAlign: "right",
  headerClassName: sortedClassFor(field, sortedField),
  cellClassName: sortedClassFor(field, sortedField),
  sortingOrder: STAT_SORT_ORDER,
  renderCell: (params) => formatGridStatValue(params.value, format),
});

const BattingTable = ({ batters, onPlayerClick }) => {
  const [sortModel, setSortModel] = useState([
    { field: "atBats", sort: "desc" },
  ]);
  const sortedField = sortModel[0]?.field;
  const rows = useMemo(() => buildBattingBoxRows(batters), [batters]);
  const columns = useMemo(
    () => [
      {
        field: "playerName",
        headerName: "Player",
        flex: 1.2,
        minWidth: 180,
        headerClassName: sortedClassFor("playerName", sortedField),
        cellClassName: sortedClassFor("playerName", sortedField),
        sortingOrder: NAME_SORT_ORDER,
        renderCell: (params) => (
          <PlayerNameCell
            playerId={params.row.playerId}
            name={params.value}
            onPlayerClick={onPlayerClick}
          />
        ),
      },
      {
        field: "position",
        headerName: "Pos",
        width: 72,
        headerClassName: sortedClassFor("position", sortedField),
        cellClassName: sortedClassFor("position", sortedField),
        sortingOrder: NAME_SORT_ORDER,
      },
      statColumn("atBats", "AB", sortedField),
      statColumn("runs", "R", sortedField),
      statColumn("hits", "H", sortedField),
      statColumn("homeRuns", "HR", sortedField),
      statColumn("rbi", "RBI", sortedField),
      statColumn("baseOnBalls", "BB", sortedField),
      statColumn("strikeOuts", "SO", sortedField),
      statColumn("avg", "AVG", sortedField, "rate", 90),
    ],
    [onPlayerClick, sortedField]
  );

  if (!batters?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Batting stats will appear once the game starts.
      </Typography>
    );
  }

  return (
    <Paper sx={{ overflow: "hidden" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        sortingMode="client"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        rowHeight={52}
        sx={getResultsGridSx(BOX_TABLE_HEIGHT)}
      />
    </Paper>
  );
};

const PitchingTable = ({ pitchers, onPlayerClick }) => {
  const [sortModel, setSortModel] = useState([
    { field: "inningsPitched", sort: "desc" },
  ]);
  const sortedField = sortModel[0]?.field;
  const rows = useMemo(() => buildPitchingBoxRows(pitchers), [pitchers]);
  const columns = useMemo(
    () => [
      {
        field: "playerName",
        headerName: "Player",
        flex: 1.2,
        minWidth: 180,
        headerClassName: sortedClassFor("playerName", sortedField),
        cellClassName: sortedClassFor("playerName", sortedField),
        sortingOrder: NAME_SORT_ORDER,
        renderCell: (params) => (
          <PlayerNameCell
            playerId={params.row.playerId}
            name={params.value}
            onPlayerClick={onPlayerClick}
          />
        ),
      },
      statColumn("inningsPitched", "IP", sortedField, "count", 90),
      statColumn("hits", "H", sortedField),
      statColumn("runs", "R", sortedField),
      statColumn("earnedRuns", "ER", sortedField),
      statColumn("baseOnBalls", "BB", sortedField),
      statColumn("strikeOuts", "SO", sortedField),
      statColumn("era", "ERA", sortedField, "era", 90),
      {
        field: "decision",
        headerName: "Dec",
        width: 80,
        headerClassName: sortedClassFor("decision", sortedField),
        cellClassName: sortedClassFor("decision", sortedField),
        sortingOrder: NAME_SORT_ORDER,
      },
    ],
    [onPlayerClick, sortedField]
  );

  if (!pitchers?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Pitching stats will appear once the game starts.
      </Typography>
    );
  }

  return (
    <Paper sx={{ overflow: "hidden" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        sortingMode="client"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        rowHeight={52}
        sx={getResultsGridSx(BOX_TABLE_HEIGHT)}
      />
    </Paper>
  );
};

const TeamStatsPanel = ({ team, onPlayerClick }) => {
  if (!team) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          Batting
        </Typography>
        <BattingTable batters={team.batters} onPlayerClick={onPlayerClick} />
      </Box>
      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          Pitching
        </Typography>
        <PitchingTable pitchers={team.pitchers} onPlayerClick={onPlayerClick} />
      </Box>
    </Box>
  );
};

const DecisionRow = ({ label, decision, onPlayerClick }) => {
  if (!decision) return null;
  const decisionLine = formatPitchingDecisionLine(decision);

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.75 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 48 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, textAlign: "right" }}>
        <Button
          variant="text"
          size="small"
          onClick={() => onPlayerClick(decision.playerId)}
          sx={{ textTransform: "none", fontWeight: 600, p: 0, minWidth: 0 }}
        >
          {decision.name}
        </Button>
        {decisionLine && (
          <Typography variant="caption" color="text.secondary" display="block">
            {decisionLine}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const GameDetailsPage = () => {
  const { gamePk } = useParams();
  const navigate = useNavigate();
  const [teamTab, setTeamTab] = useState(0);
  const { data, isLoading, error } = useGetGameDetailsQuery(gamePk, {
    skip: !gamePk,
  });

  const game = data?.success ? data.data : null;
  const statusChip = getStatusChip(game?.status);

  const handlePlayerClick = (playerId) => {
    const path = getPlayerPath(playerId);
    if (path) {
      navigate(path);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !game) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">
          {error?.data?.message || error?.message || "Failed to load game details"}
        </Alert>
      </Container>
    );
  }

  const awayName = game.teams?.away?.team?.name || "Away";
  const homeName = game.teams?.home?.team?.name || "Home";

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 2,
          background: `linear-gradient(180deg, ${brandRgba(BRAND_COLORS.cyan, 0.08)} 0%, rgba(20,20,20,0) 100%)`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Game #{game.gamePk}
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
              {awayName} @ {homeName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatGameDate(game.gameDate, game.officialDate)}
              {game.venue?.name ? ` • ${game.venue.name}` : ""}
            </Typography>
          </Box>
          <Chip label={statusChip.label} color={statusChip.color} variant="outlined" />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          <TeamScoreBlock
            team={game.teams.away}
            side="away"
            score={game.teams.away.score}
            isWinner={game.teams.away.isWinner}
          />
          <Typography variant="h4" color="text.secondary" sx={{ px: 1 }}>
            @
          </Typography>
          <TeamScoreBlock
            team={game.teams.home}
            side="home"
            score={game.teams.home.score}
            isWinner={game.teams.home.isWinner}
          />
        </Box>
      </Paper>

      <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" } }}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Linescore
          </Typography>
          <LinescoreTable linescore={game.linescore} awayName={awayName} homeName={homeName} />
          {game.linescore?.inningState && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
              {game.linescore.currentInningOrdinal || `Inning ${game.linescore.currentInning}`} • {game.linescore.inningState}
            </Typography>
          )}
        </Paper>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
              Pitching Decisions
            </Typography>
            {!game.decisions?.winner && !game.decisions?.loser && !game.decisions?.save ? (
              <Typography variant="body2" color="text.secondary">
                Decisions will appear once the game is final.
              </Typography>
            ) : (
              <Box>
                <DecisionRow label="W" decision={game.decisions.winner} onPlayerClick={handlePlayerClick} />
                <DecisionRow label="L" decision={game.decisions.loser} onPlayerClick={handlePlayerClick} />
                <DecisionRow label="S" decision={game.decisions.save} onPlayerClick={handlePlayerClick} />
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mt: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Box Score
        </Typography>
        <Tabs
          value={teamTab}
          onChange={(_, value) => setTeamTab(value)}
          variant="fullWidth"
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label={awayName} />
          <Tab label={homeName} />
        </Tabs>
        {teamTab === 0 ? (
          <TeamStatsPanel team={game.teams.away} onPlayerClick={handlePlayerClick} />
        ) : (
          <TeamStatsPanel team={game.teams.home} onPlayerClick={handlePlayerClick} />
        )}
      </Paper>
    </Container>
  );
};

export default GameDetailsPage;
